import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Menu } from "antd5";

import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import { FilterableBuyerTable } from "components/buyers/FilterableBuyerTable";
import { DEFAULT_BUYER_FILTERS } from "components/buyers/useFilterableBuyerTableState";
import { FilterableFrameworks } from "components/framework/FilterableFrameworks";
import {
  DEFAULT_FILTERS as DEFAULT_FRAMEWORK_FILTERS,
  FrameworkColumns,
} from "components/framework/utils";
import { FilterableNoticeTable } from "components/notices/FilterableNoticeTable";
import { useFilterableNoticeTableState } from "components/notices/useFilterableNoticeTableState";
import { EMPTY_FILTERS, NoticeColumns, NoticeFilters } from "components/notices/utils";
import {
  getTabConfigFromLocation,
  labelledRoutes,
  PrimaryTab,
  SecondaryTab,
} from "components/supplier_details/supplier_utils";
import { useSupplierFromRoute } from "components/supplier_details/SupplierDetailLayout";
import SupplierHeader from "components/supplier_details/SupplierHeader";
import BetaIcon from "components/ui/beta_icon/BetaIcon";
import {
  BUYER_LIST_PERMISSION_ACTIONS,
  useBuyerLists,
} from "lib/hooks/api/buyer_lists/useBuyerLists";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import PaywallStar from "lib/icons/PaywallStar";
import { usePaywall } from "lib/providers/Paywall";
import * as tracking from "lib/tracking";
import { EventData, EventNames, logEvent, TrackingProvider } from "lib/tracking";
import {
  LoggedEntityType,
  NoticesStagesv2,
  OrgSortBy,
  SupplierMentionType,
} from "lib/types/graphQLEnums";
import { ColumnSetting } from "lib/types/models";
import { stripPathPrefix } from "lib/utils";

import css from "./SupplierProfilePage.module.scss";

const AWARDS_COLUMNS: NoticeColumns[] = [
  "relevance_score",
  "indicator",
  "title",
  "expiry_date",
  "value",
  "stage",
  "qualification",
  "assignee",
];

const FRAMEWORK_COLUMNS: ColumnSetting<FrameworkColumns>[] = [
  "signalScore",
  "title",
  "stage",
  "suppliers",
  "startDate",
  "endDate",
  "awardType",
].map((c) => {
  return {
    field: c,
    visible: true,
  } as ColumnSetting<FrameworkColumns>;
});

export const DEFAULT_SUPPLIER_NOTICES_SORT: NoticeFilters["sort"] = {
  field: "relevance_score",
  order: "DESC",
};

export function SupplierProfilePage() {
  const supplier = useSupplierFromRoute();
  useLogEntityViewOnOpen(LoggedEntityType.Supplier, supplier?.guid);

  useEffect(() => {
    if (!supplier) return;
    document.title = `${supplier.name} | Stotles`;
    tracking.pageView("Supplier Profile", {
      "Supplier name": supplier.name,
      "Supplier ID": supplier.id,
    });
  }, [supplier]);

  if (!supplier) return null;

  return <SupplierProfileContent supplier={supplier} />;
}

type ContentProps = {
  supplier: NonNullable<ReturnType<typeof useSupplierFromRoute>>;
};

/** Inner content of the supplier profile; exported so tests can inject the supplier directly. */
export function SupplierProfileContent({ supplier }: ContentProps) {
  const { data: currentUser } = useCurrentUser();
  const { data: buyerLists } = useBuyerLists(BUYER_LIST_PERMISSION_ACTIONS.CREATE_ENTRIES, {
    enabled: !!currentUser,
  });

  const subscription = useSubscription();
  const { openPaywallModal } = usePaywall();

  const showFrameworksPaywall = !subscription.hasDataTypes("FRAMEWORKS");

  const navigate = useNavigate();
  // Reactive pathname so memory-history tests stay in sync; window.location is a fallback.
  const routerPathname = useRouterState({ select: (s) => s.location.pathname });
  const supplierPrefix = `/suppliers/${supplier.guid}`;
  const location = stripPathPrefix(routerPathname, supplierPrefix, "/");

  const setLocation = useCallback(
    (route: string) => {
      void navigate({
        to: `/suppliers/${supplier.guid}${route}`,
        search: (prev: Record<string, unknown>) => prev,
      });
    },
    [navigate, supplier.guid],
  );

  const tabConfig = useMemo(
    () => getTabConfigFromLocation(location as PrimaryTab | SecondaryTab),
    [location],
  );

  const { setNoticeTableFilters } = useFilterableNoticeTableState();

  const handleNavigation = useCallback(
    (loc: PrimaryTab | SecondaryTab, eventName: EventNames, eventData: EventData) => {
      setNoticeTableFilters(undefined);
      setLocation(loc);
      logEvent(eventName, eventData);
    },
    [setNoticeTableFilters, setLocation],
  );

  const getDefaultNoticeFilters = (): NoticeFilters => {
    switch (tabConfig.secondary) {
      case SecondaryTab.AWARDS:
        return {
          ...EMPTY_FILTERS,
          stage: [
            NoticesStagesv2.AwardedContract,
            NoticesStagesv2.ExpiredContract,
            NoticesStagesv2.CancelledAward,
          ],
          supplierMentionType: SupplierMentionType.DirectlyAwarded,
          supplierGuids: [supplier.guid],
          sort: DEFAULT_SUPPLIER_NOTICES_SORT,
          includeHiddenNotices: true,
        };
      case SecondaryTab.MENTIONS:
        return {
          ...EMPTY_FILTERS,
          supplierMentionType: SupplierMentionType.MentionedInOnly,
          supplierGuids: [supplier.guid],
          sort: DEFAULT_SUPPLIER_NOTICES_SORT,
          includeHiddenNotices: true,
        };
      case SecondaryTab.UPCOMING_EXPIRIES:
        return {
          ...EMPTY_FILTERS,
          stage: [
            NoticesStagesv2.AwardedContract,
            NoticesStagesv2.ExpiredContract,
            NoticesStagesv2.CancelledAward,
          ],
          suppliers: [supplier.id],
          supplierGuids: [supplier.guid],
          expiryDate: { filter: { relativeFrom: "PT0S" }, hideNulls: true },
          signals: [], // TODO: Remove during layer-cake cleanup
          includeHiddenNotices: true,
        };
      default:
        return {
          ...EMPTY_FILTERS,
          suppliers: [supplier.id],
          supplierGuids: [supplier.guid],
          includeHiddenNotices: true,
        };
    }
  };

  return (
    <TrackingProvider data={{ "Supplier name": supplier.name, "Supplier ID": supplier.id }}>
      <DetailsPage>
        <DetailsHeader className={css.noPaddingBottom}>
          <SupplierHeader
            supplier={supplier}
            handleNavigation={handleNavigation}
            currentTabPath={location}
          />
          <TrackingProvider
            data={{ "Context source": labelledRoutes[tabConfig.secondary || tabConfig.primary] }}
          >
            <Menu mode="horizontal" selectedKeys={[tabConfig.primary]} className={css.menuTabs}>
              <Menu.Item
                key={PrimaryTab.NOTICES}
                onClick={() =>
                  handleNavigation(PrimaryTab.NOTICES, EventNames.tabChanged, {
                    "Context source": "Primary Tab",
                    "Tab Selected": labelledRoutes[PrimaryTab.NOTICES],
                  })
                }
                className={css.menuTabItem}
              >
                Notices
              </Menu.Item>
              {currentUser ? (
                <>
                  <Menu.Item
                    key={PrimaryTab.FRAMEWORKS}
                    onClick={() => {
                      if (showFrameworksPaywall) {
                        openPaywallModal("FRAMEWORKS");
                      } else {
                        handleNavigation(PrimaryTab.FRAMEWORKS, EventNames.tabChanged, {
                          "Context source": "Primary Tab",
                          "Tab Selected": labelledRoutes[PrimaryTab.FRAMEWORKS],
                        });
                      }
                    }}
                    className={css.menuTabItem}
                  >
                    Frameworks{showFrameworksPaywall && <PaywallStar />}{" "}
                    <BetaIcon betaFeature="frameworks" />
                  </Menu.Item>

                  <Menu.Item
                    key={PrimaryTab.BUYERS}
                    onClick={() =>
                      handleNavigation(PrimaryTab.BUYERS, EventNames.tabChanged, {
                        "Context source": "Primary Tab",
                        "Tab Selected": labelledRoutes[PrimaryTab.BUYERS],
                      })
                    }
                    className={css.menuTabItem}
                  >
                    Buyers
                  </Menu.Item>
                </>
              ) : null}
            </Menu>
          </TrackingProvider>
        </DetailsHeader>
        <DetailsContent className={css.supplierProfileContent}>
          {tabConfig.primary === PrimaryTab.FRAMEWORKS ? (
            showFrameworksPaywall ? (
              <></>
            ) : (
              <FilterableFrameworks
                defaultColumns={FRAMEWORK_COLUMNS}
                defaultFilters={{
                  ...DEFAULT_FRAMEWORK_FILTERS,
                  supplierIds: [supplier.guid],
                }}
                hiddenFilterSections={["suppliers"]}
              />
            )
          ) : tabConfig.primary === PrimaryTab.BUYERS ? (
            <FilterableBuyerTable
              defaultFilters={{
                ...DEFAULT_BUYER_FILTERS,
                supplierGuids: [supplier.guid],
                buyerLists:
                  tabConfig.secondary === SecondaryTab.SAVED_BUYERS
                    ? buyerLists?.all?.map((buyerList) => buyerList.id) || []
                    : [],
              }}
              defaultSortOrder={{ field: OrgSortBy.AwardCount, order: "DESC" }}
              hiddenSections={["signals", "suppliers"]}
              isSupplierRelationship
            />
          ) : (
            <FilterableNoticeTable
              defaultFilters={getDefaultNoticeFilters()}
              defaultColumns={AWARDS_COLUMNS}
              hiddenSections={["suppliers"]}
              paywallEnabled={true}
              location={"suppliers"}
            />
          )}
        </DetailsContent>
      </DetailsPage>
    </TrackingProvider>
  );
}
